/**
 * Inline empty/error state for the EHR monitoring views. Stays inside the page
 * chrome so a monitoring failure reads as "couldn't load" rather than as an
 * empty, healthy-looking result.
 */
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { commonMessages } from '../../messages';

interface StatePanelProps {
  variant: 'error' | 'empty';
  title: string;
  body?: string;
  /** Rendered as a Retry button on the error variant only. */
  onRetry?: () => void;
}

export default function StatePanel({
  variant,
  title,
  body,
  onRetry,
}: StatePanelProps) {
  const { formatMessage } = useIntl();
  const isError = variant === 'error';

  return (
    <Box
      role={isError ? 'alert' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
        px: 3,
        py: 6,
      }}
    >
      {isError ? (
        <Icon name="triangle-exclamation" color="error.main" type="fas" />
      ) : null}

      <Typography
        variant="subtitle1"
        fontWeight="bold"
        color={isError ? 'error.main' : 'text.primary'}
      >
        {title}
      </Typography>

      {body ? (
        <Typography variant="body2" sx={{ maxWidth: 460 }}>
          {body}
        </Typography>
      ) : null}

      {isError && onRetry ? (
        <Button
          variant="outlined"
          size="small"
          onClick={onRetry}
          sx={{ mt: 1.5 }}
        >
          {formatMessage(commonMessages.retry)}
        </Button>
      ) : null}
    </Box>
  );
}
